<script setup>
/* eslint semi: 1 */
import { onMounted, onUnmounted, getCurrentInstance, ref, computed, inject, watch } from 'vue';
import { FwbButton, FwbButtonGroup, FwbTooltip } from 'flowbite-vue';
import { useI18n } from 'vue-i18n';
import AuthMixin from '@/components/mixins/auth-mixin';
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin';
import TagSlider from '@/components/common/tag-slider/tag-slider-tmpl.vue';
import PopIdentifierSlider from '@/components/common/pop-identifier-slider/pop-identifier-slider-tmpl.vue';
import MediaGridList from '@/components/common/media/media-grid-list.vue';
import MediaTableList from '@/components/common/media/media-table-list.vue';
import MediaListFooter from '@/components/common/media/media-list-footer.vue';
import MediaFilter from '@/components/common/media/media-filter.vue';
import MediaSearch from '@/components/common/media/media-search.vue';
import MediaSort from '@/components/common/media/media-sort.vue';
import MediaCanvasSelector from '@/components/common/media/media-canvas-selector.vue';
import ImagePreviewDialog from '@/components/common/image-preview-dialog/image-preview-dialog-tmpl';
import VideoPlayerDialog from '@/components/common/video-player-dialog/video-player-dialog-tmpl';
import AudioPlayer from '@/components/common/audio-player/audio-player-tmpl';
import MediaMenu from '@/components/common/media-menu/media-menu-tmpl';
import LedResolutionMenu from '@/components/common/led-resolution-menu/led-resolution-menu-tmpl';
import FileUploaderProgress from '@/components/common/file-uploader/file-uploader-progress.vue';
import CONST from '@/globals/constants';
import Canvas, { FindPlayerSchedulesByLEDTag as findPlayerSchedulesByLEDTag } from '@/components/pages/player/player-setup/player-data-canvas';

import { useMediaListPage } from '@/composables/media/use-media-list-page';
import { useMediaQuery } from '@/composables/media/use-media-query';
import { useMediaSelection } from '@/composables/media/use-media-selection';
import { useClickOutside } from '@base-shared/composables/use-click-outside';
import { useMediaUpdate } from '@/composables/media/use-media-update';
import { useUserSchedules } from '@/composables/media/use-user-schedules';
import { useMediaContextMenu } from '@/composables/media/use-media-context-menu';
import { useMediaPreviewEvents } from '@/composables/media/use-media-preview-events';
import { tagsStore } from '@/pinia/stores/tagsStore';
import { hasAccessToLEDPage, hasPermissionToCreateLEDProject, hasPermissionToCreateLEDResolutionTag } from '@/utils/led-page-accessibility.js';
import { hasAccessToVideowallPage, hasPermissionToCreateVideowallProject } from '@/utils/videowall-page-accessibility.js';
import { useFileUploaderStore } from '@/pinia/stores/file-upload/fileUploaderStore';
import { TARGET_COMPONENTS } from '@/composables/file-upload/use-file-uploader';
import EventBus from '@/components/mixins/event-bus-mixin';

defineOptions({
  mixins: [AuthMixin, UnsavedDataMixin]
});

// Use component instance to call methods from the AuthMixin
const { proxy } = getCurrentInstance();
const { t } = useI18n();
const $api = inject('$api');
const $utils = inject('$utils');
const $router = inject('$routerInstance');
const $currentUser = inject('$currentUser');
const $apiError = inject('$apiError');
const mediaListPage = useMediaListPage();
const mediaQuery = useMediaQuery();
const mediaSelection = useMediaSelection();
const mediaUpdate = useMediaUpdate();
const userSchedules = useUserSchedules();
const mediaPreviewEvents = useMediaPreviewEvents();
const tagStore = tagsStore();
const fileUploaderStore = useFileUploaderStore();

const props = defineProps({
  mediaComponentAs: {
    type: String,
    default: CONST.MEDIA_COMPONENT_AS_MEDIA_FILES,
    validator: value => [
      CONST.MEDIA_COMPONENT_AS_MEDIA_FILES,
      CONST.MEDIA_COMPONENT_AS_ALERT_FILES,
      CONST.MEDIA_COMPONENT_AS_LED_FILES,
      CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES,
    ].includes(value)
  },
});

defineEmits(['toggle-to-legacy-media']);

// References for handling click outside to clear selection and close context menu
const mediaListContainerRef = ref(null);
const mediaMenuContainerRef = ref(null);
const tagSliderElRef = ref(null);
const popIdSliderElRef = ref(null);

const {
  isContextMenuVisible,
  isLedResolutionVisible,
  isVideowallResolutionVisible,
  tagsSummary,
  popIdsSummaryByPage,
  handleMediaMenuChange,
  showLedResolutionMenu,
  showVideowallResolutionMenu,
  hideResolutionMenus,
  handleLedResolutionMenuChange
} = useMediaContextMenu({ tagSliderElRef, popIdSliderElRef });

// Clear the selection when clicking outside the media page container.
useClickOutside([
  mediaListContainerRef,
  mediaMenuContainerRef,
  // NOTE: Since a lot of child components under MediaMenu
  // uses element plus with popper and teleport them to
  // body by default, also include the class names as
  // targets for it to not trigger click outside.
  '.add-new-tag-as-dropdown',
  '.custom-tags-auto-complete-dropdown',
  '.custom-popIds-auto-complete-dropdown',
  '.schedule-time-tag-options-popper',
  '.playtime-datetime-picker',
  '.validity-property-date-range',

  // Global notification dialog component
  '#customNotification'
], () => {
  // Trigger checkUnsavedData before clearing selection
  proxy.checkUnsavedData({
    nextAction: () => mediaSelection.clearSelection()
  });
});

// State to control when the media page can start fetching
// data and showing the tag slider and pop identifier slider
// filters. This is to ensure `proxy.isAuthenticated` is
// checked.
const isPageReady = ref(false);
const isTagSliderReady = ref(false);
const isPopIdSliderReady = ref(false);

const confirmForceToDeleteMedia = ref({ showDialog: false, items: [], confirmed: false });
const hideMediaMenuComponentTemplate = ref(false);

const pageNotification = ref(null);

// State to determine if the media page is fully hydrated
// and ready to display and fetch media.
const isPageHydrated = computed(() => isPageReady.value && isTagSliderReady.value && (isPopIdSliderReady.value || !showPopIdsSliderFilter.value));

// List view mode preference for the media page, either 'grid' or 'table'.
const listViewMode = computed(() => mediaListPage.listViewMode.value);

const loadedItems = computed(() => mediaListPage.items.value ?? []);
const currentPage = computed(() => mediaListPage.pageIndex.value + 1);
const totalPages = computed(() => mediaListPage.totalPagesLoaded.value);
const hasPreviousPage = computed(() => mediaListPage.hasPreviousPage.value);
const hasNextPage = computed(() => mediaListPage.hasNextPage.value);
const isFetching = computed(() => mediaListPage.isFetching.value || tagStore.isFetching);

const selectionDetails = mediaSelection.selection;
const hasSelection = mediaSelection.hasSelection;
const selectedIds = mediaSelection.selectedIds;
const isFetchingDetails = mediaSelection.isFetchingDetails;

const enableDelete = computed(() => $currentUser.getRights().userCreate.deleteMedia ?? false);
const enableCopy = computed(() => $currentUser.getRights().userCreate.editProject ?? false);
const enableEditProject = computed(() => $currentUser.getRights().userCreate.editProject ?? false);

const mediaOrientation = computed(() => mediaQuery.orientation.value);
const isLEDMediaPage = computed(() => props.mediaComponentAs === CONST.MEDIA_COMPONENT_AS_LED_FILES);
const isVideowallMediaPage = computed(() => props.mediaComponentAs === CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES);
const isAlertsPage = computed(() => props.mediaComponentAs === CONST.MEDIA_COMPONENT_AS_ALERT_FILES);

const showListLoader = computed(() => mediaListPage.isFetching.value);
const showTagsSliderFilter = computed(() => isPageReady.value);
const showPopIdsSliderFilter = computed(() => isPageReady.value && $currentUser.hasFeatureProofOfPlayExtended());
/** Checks if the aside panel should be visible */
const showResolutionMenu = computed(() => isLedResolutionVisible.value || isVideowallResolutionVisible.value);

/**
 * Checks if the aside panel should be visible.
 * TODO(media-v2-cleanup): Temporarily set to always true for now to show
 * Legacy view switcher. This will be removed once the
 * Legacy view switcher is removed.
 *
 * Replace with:
 * const showAside = computed(() => isContextMenuVisible.value || showResolutionMenu.value);
 */
const showAside = computed(() => true);

/** Checks if create project btn should be visible (LED/Videowall) */
const showCreateProjectBtn = computed(() => {
  if (isLEDMediaPage.value) {
    return hasPermissionToCreateLEDProject($currentUser);
  } else if (isVideowallMediaPage.value) {
    return hasPermissionToCreateVideowallProject($currentUser);
  }

  return false;
});
/** Checks if create canvas resolution should be visible */
const showCreateLEDResolution = computed(() => {
  // Hide if the resolution menu is already visible
  if (showResolutionMenu.value) return false;
  if (isLEDMediaPage.value) {
    return hasPermissionToCreateLEDResolutionTag($currentUser);
  } else if (isVideowallMediaPage.value) {
    return hasPermissionToCreateVideowallProject($currentUser);
  }

  return false;
});

/**
 * Passes the selected LED/Videowall resolution to MediaGridList so it can
 * switch into pixel-based card sizing mode. Returns null for non-LED pages so
 * MediaGridList falls back to the standard CSS fractional layout.
 */
const activeLedTag = computed(() => {
  const selectedTag = mediaQuery.selectedLedTag.value;
  if (isLEDMediaPage.value) {
    return tagStore.ledTags.length > 0
      ? tagStore.ledTags.find(tag => tag.value === selectedTag?.value) ?? selectedTag
      : selectedTag;
  } else if (isVideowallMediaPage.value) {
    return tagStore.videowallTags.length
      ? tagStore.videowallTags.find(tag => tag.value === selectedTag?.value) ?? selectedTag
      : selectedTag;
  }

  return null;
});

/**
 * Reactive list of user schedules, filtered for LED/Videowall
 * pages to only include player schedules with matching
 * LED/Videowall tags.
 */
const userSchedulesList = computed(() => {
  const allSchedules = userSchedules.allSchedules.value ?? [];
  const activeTagValue = activeLedTag.value?.value;

  if (activeTagValue && (isLEDMediaPage.value || isVideowallMediaPage.value)) {
    return findPlayerSchedulesByLEDTag(activeTagValue, allSchedules);
  }

  return allSchedules;
});

/**
 * Tracks uploaded files from the Vuex file uploader store state.
 * Composition API equivalent of the Options API mapState used in the legacy media page:
 * @see {@link src/ui/components/pages/create/media/media.js} - `fileUploaderState` computed property
 */
const fileUploaderState = computed(() => fileUploaderStore.uploadedFiles);

/**
 * Watches the Vuex file uploader state for changes and refreshes
 * the media page when new files are processed.
 * This is equivalent to the Options API watch on `fileUploaderState` in the legacy media page:
 * @see {@link src/ui/components/pages/create/media/media.js} - `watch: { fileUploaderState }`
 *
 * This ensures that after uploading new files, the media list is
 * updated to reflect the new additions.
 */
watch(fileUploaderState, async (newVal) => {
  if (newVal.prevAction === 'processNewFilesWithInstance') {
    // Only trigger the notification and media list refresh if there are files processed.
    if (newVal.files.length <= 0) return;
    /**
     * To determine whether to show mediaUpload notification
     * message to inform the user that the newly uploaded
     * media may not appear at the top of the list due to the
     * default sort order or active filters.
     */
    if (!mediaQuery.isDefaultSort.value || mediaQuery.hasActiveFilters.value) {
      pageNotification.value = {
        id: 'media-upload',
        title: t('media.mediaUploadSuccessTitle'),
        message: t('media.mediaUploadSuccessMessage'),
        type: 'info',
        duration: 10000,
        icon: 'flowbite:check-circle-solid',
        closable: true
      };
    }

    // Extract resourceIds from the uploaded files and trigger
    // the event to process them in mediaListPage.
    const resourceIds = newVal.files.map(file => file?._edit?.resourceId).filter(Boolean);
    await mediaListPage.handleUploadProcessNewFilesEvent(resourceIds);
    await refreshMediaPage();
  }
}, {
  deep: true
});

/**
 * Navigates to the given page. The Paginator emits 1-based page numbers
 * (matching what the user sees), but the store uses 0-based indices,
 * hence the subtraction.
 * This also clears the media selection to avoid confusion when
 * navigating between pages
 * @param {number} page - 1-based page number emitted by the Paginator
 */
async function goToMediaPage (page) {
  // Reset media preview events when navigating to a different page
  mediaPreviewEvents.resetMediaPreviewEvents();
  // Reset selection when navigating to a different page
  mediaSelection.clearSelection();
  await mediaListPage.goToPage(page - 1);
}

/**
 * Triggers tag and pop id sliders to refresh their lists.
 */
function refreshSliderComponents () {
  tagSliderElRef?.value?.refreshTagList?.();
  popIdSliderElRef?.value?.refreshPopIdList?.();
}

/**
 * Handles events emitted from the tag-slider and
 * pop-id-slider components (e.g. tags are fetched, tags
 * are deleted, etc.).
 * @param {Object} event - the event emitted from the slider component
 * @param {string} event.action - the specific event action
 */
async function handleSlidersEvent ({ action }) {
  if (
    action === CONST.EMIT_EVENT_DELETED_TAG_FROM_SLIDER ||
    action === CONST.EMIT_EVENT_DELETED_POPID_FROM_SLIDER
  ) {
    // When tag or pop id is deleted from the slider, it
    // leaves the main loader on. Handle it here for now.
    proxy.$utils.mainLoader(false);
    await refreshMediaPage();
    return;
  }

  if (
    action === CONST.EMIT_EVENT_NEW_SELECTED_TAGS_FROM_SLIDER ||
    action === CONST.EMIT_EVENT_NEW_SELECTED_POP_ID_FROM_SLIDER
  ) {
    // When new tags are selected from the slider, go back to
    // page 0 and merge staged updates.
    await refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true });
    return;
  }

  if (
    action === CONST.EMIT_EVENT_DONE_FETCHING_TAGS_FROM_SLIDER ||
    action === CONST.EMIT_EVENT_REFETCH_PARENT_COMPONENT_LIST
  ) {
    // Set isTagSliderReady to true when the slider is done
    // fetching tags. Required before fetching or refreshing
    isTagSliderReady.value = true;
  } else if (
    action === CONST.EMIT_EVENT_LIST_OF_POP_IDS_FROM_SLIDER ||
    action === CONST.EMIT_EVENT_REFETCH_PARENT_COMPONENT_LIST
  ) {
    // Set isPopIdSliderReady to true when the slider is done
    // fetching tags. Required before fetching or refreshing
    isPopIdSliderReady.value = true;
  }

  await refreshMediaPage();
}

/**
 * Handles a card click from either the grid or table list.
 * LED/Videowall placeholder cards trigger creation flows (handled later);
 * all other items are forwarded to the media selection composable.
 * @param {Object} item - The media item that was clicked
 */
function handleCardClick (item) {
  // Reset media preview events.
  mediaPreviewEvents.resetMediaPreviewEvents();
  proxy.checkUnsavedData({
    nextAction: async () => {
      // Reset media menu template visibility only after the user confirms any unsaved-data dialog
      hideMediaMenuComponentTemplate.value = false;
      await mediaSelection.selectMedia(item, mediaListPage.items.value);
    }
  });
}

/**
 * Handles force deletion of media items that are still in use (blocked).
 * @param {Object[]} blocked - list of media items that are blocked from deletion due.
 */
async function handleForceDeleteMedia (blocked) {
  // Show force delete confirmation.
  const options = { mediaOrientation: mediaOrientation.value };
  const forceConfirmed = await mediaUpdate.confirmForceDeleteItems(blocked, options);
  if (!forceConfirmed) return;

  // Initiate force delete request for the media items.
  proxy.$utils.mainLoader(true);
  const { failed } = await mediaUpdate.forceDeleteMedia(blocked, { isAlert: isAlertsPage.value });
  proxy.$utils.mainLoader(false);
  if (failed?.length > 0) {
    // If something failed, let the users allow retrying
    // instead of automatically refreshing the media list
    // which would remove current selection.
    // Succeeded items will be removed to the local list.
    return;
  }

  // Proceed normally.
  refreshSliderComponents();
}

/**
 * Handles the delete icon click on a media card or table row.
 * If the item(s) are still in use, it should be handled with
 * force deletion.
 * @param {Object} item - The media item to delete
 */
async function handleDeleteClick (item) {
  // Check if there are multiple items selected and if the
  // clicked item is part of the selection.
  const itemsToDelete = selectionDetails.value.length > 1
    ? selectionDetails.value
    : [item];

  // Show confirmation dialog before deleting media items.
  const confirmed = await mediaUpdate.confirmDeleteItems(itemsToDelete);
  if (!confirmed) return;

  // Initiate delete request for the media items.
  proxy.$utils.mainLoader(true);
  const { blocked, failed } = await mediaUpdate.deleteMedia(itemsToDelete, { isAlert: isAlertsPage.value });
  proxy.$utils.mainLoader(false);
  if (failed?.length > 0) {
    // If something failed, prioritize showing an error
    // notification for the failed deletions. Blocked items
    // can be retried by the users so error notification and
    // force delete doesn't conflict with each other in the UI.
    // Succeeded items will be removed to the local list.
    return;
  } else if (blocked?.length > 0) {
    // Handle blocked items if no failed items.
    await handleForceDeleteMedia(blocked);
    return;
  }

  // Nothing blocked and failed, proceed normally.
  refreshSliderComponents();
}

/**
 * Handles the copy icon click on a media card or table row.
 * Duplicates the project by calling the copy flow in use-media-update:
 * GET /project → POST /project (copy) → GET /media (new) → prepend to list.
 * Only callable for svg/html project items.
 * @param {Object} item - The media item to duplicate
 */
async function handleCopyClick (item) {
  proxy.$utils.mainLoader(true);
  await mediaUpdate.copyMedia(item);
  proxy.$utils.mainLoader(false);
}

/**
 * Handles the edit icon click on a media card or table row.
 * Redirects to the project editor for svg/html project items.
 * Audio playlists are redirected to the audio project editor.
 * @param {Object} item - The media item to edit
 */
function handleEditClick (item) {
  let redirectPath = item.type === 'audio-playlist'
    ? `/project-audio/${item.resourceId}/edit-frame`
    : `/project-edit/${item.resourceId}/edit-frame`;

  const encodedQueryString = $utils.encodeQueryString($router?.currentRoute?.value?.query);
  if (encodedQueryString && redirectPath.includes('/project-edit')) {
    redirectPath = `${redirectPath}?${encodedQueryString}`;
  }

  $router.push(redirectPath);
}

/**
 * Handles updating the name of a media item by calling
 * the mediaUpdate composable to send the API request.
 * This is emitted from the MediaGridList or MediaTableList
 * when the user clicks the edit name icon on a media card/row,
 * and submits a new name in the inline edit input.
 * @param data - contains the resourceId and new name value
 */
async function handleMediaRename (data) {
  const { resourceId, newName } = data;
  const mediaItem = mediaListPage.items.value.find(item => item.resourceId === resourceId);

  if (!mediaItem) {
    return;
  }

  await mediaUpdate.renameMedia(mediaItem, newName);
}

/**
 * Hook function for draggable as dropzone for `tags-shared` group.
 * @param {Object} data - VueDraggable add event
 * @param {Object} data.event - the original VueDraggable event object
 * @param {Object} data.media - the media item that is the target of the drag event (i.e. the media item that the tag or popId is being dragged to)
 */
async function handleDragTagToMedia (data) {
  if (!data?.event?.item || !data?.media?.resourceId) {
    return;
  }

  const draggedEl = data.event.item; // element being dragged
  const draggedDataset = draggedEl?.dataset; // data-* attributes
  const dataDraggableType = draggedDataset?.draggableType ?? draggedEl.getAttribute('data-draggable-type'); // 'tag' or 'popId'
  const dataDraggableValue = draggedDataset?.draggableValue ?? draggedEl.getAttribute('data-draggable-value'); // tag or popId value
  if (!draggedEl || !dataDraggableType || !dataDraggableValue || !data.media?.resourceId) {
    return;
  }

  // Check if media is selected first, if not, select the media before applying the tag or popId update.
  // This is to ensure that the updated media details are fetched and stored in the selection store,
  // which is needed for the update operations later.
  let media = selectionDetails.value.find(media => media.resourceId === data.media.resourceId);
  // Set media selected if not
  if (!media) {
    proxy.$utils.mainLoader(true);
    await mediaSelection.selectMedia(data.media, mediaListPage.items.value);
    proxy.$utils.mainLoader(false);
  }

  // Get updated media details from selection to ensure that schedules data is included
  media = selectionDetails.value.find(media => media.resourceId === data.media.resourceId);

  if (!media) {
    return;
  }

  const tag = dataDraggableType === 'tag' ? dataDraggableValue : null;
  const popId = dataDraggableType === 'popId' ? dataDraggableValue : null;

  // All tag or PopId should not be added in any audio files
  if (
    tag === CONST.ALL_TAG_VALUE ||
    (popId && ['audio', 'audio-playlist'].includes(media.type))
  ) {
    return;
  }

  if (tag && !media.tags?.includes(tag)) { // Tag
    await mediaUpdate.updateMediaTags(
      media,
      [...(media.tags ?? []), tag]
    );
  } else if (popId && media.popId !== popId) { // PopId
    await mediaUpdate.updateMediaPopId(media, popId);
  }
}

/**
 * It checks if there is an active LED tag with a query
 * string value and constructs a route object whether it's
 * an LED/Videowall, and navigates to create project page.
 */
async function handleCreateLEDClick () {
  if (
    !activeLedTag.value?.dimension ||
    (!isLEDMediaPage.value && !isVideowallMediaPage.value)
  ) {
    return;
  }

  const width = activeLedTag.value?.width ?? 1080;
  const height = activeLedTag.value?.height ?? 1080;
  // If there are no LED tags, create a default one before
  // navigating to the create page.
  if (!tagStore.ledTags?.length) {
    proxy.$utils.mainLoader(true);
    const { error } = await new Canvas($api).createLED('', width, height);
    proxy.$utils.mainLoader(false);
    if (error) {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('playerSetup.CannotCreateLEDTag')
        }
      });
      return;
    }
  }

  const route = {
    path: '/template/default/led',
    query: { dimensions: activeLedTag.value.dimension }
  };

  // Use the query string value from the active LED tag if
  // it exists, otherwise fallback to using the width and
  // height to ensure create page have correct states.
  const queryValue = activeLedTag.value?.queryStringValue
    ? activeLedTag.value?.queryStringValue
    : `${width}x${height}`;

  if (isLEDMediaPage.value) {
    route.query.ledTags = queryValue;
  } else if (isVideowallMediaPage.value) {
    route.query.videowallTags = queryValue;
  }

  $router.push(route);
}

/**
 * Handles the removal of a LED/Videowall tag. If the deleted
 * tag is currently active in the media query, it clears the
 * active tag and refreshes the media page to reflect.
 * @param tag - the LED/Videowall tag being removed from the slider
 */
async function handleLedTagRemove (tag) {
  if (!tag?.value) return;

  $utils.mainLoader(true);
  await tagStore.deleteTag(tag, isVideowallMediaPage.value ? 'videowall' : 'led');
  $utils.mainLoader(false);

  if (tagStore.error) {
    $apiError.handleAPIRequestError(tagStore.error, { showDialogNotification: true });
    return;
  }

  // Handle removing the led dimension to mediaListStore cache.
  if (tag.dimension) {
    mediaListPage.handleLedTagRemove(tag.dimension, isVideowallMediaPage.value ? 'videowall' : 'led');
  }

  if (tag.value !== activeLedTag.value?.value) return;

  mediaQuery.clearAll();
  await refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true });
}

function handleNewResolutionClick (type) {
  if (type === 'led') {
    showLedResolutionMenu();
  } else if (type === 'videowall') {
    showVideowallResolutionMenu();
  } else {
    hideResolutionMenus();
  }
}

/**
 * Initializes/Resets the media list to page 0 and re-fetches
 * using the current filter state. Call this after any
 * `apply()` or `clearAll()` from a filter composable, or
 * after any direct mutation of the query store.
 * @param {Object} opts - (optional) options for refreshing the media page
 * @param {boolean} opts.force - (optional) whether to force fetch
 * @param {boolean} opts.goToFirstPage - (optional) whether to reset to page 0
 * @param {boolean} opts.mergeStagedUpdates - (optional) whether to resolve and merge
 * any staged updates from mediaListStore.stagedMediaUpdates to the master list
 */
async function refreshMediaPage (opts = {}) {
  // Reset media preview events on refresh.
  mediaPreviewEvents.resetMediaPreviewEvents();
  if (!isPageHydrated.value) return;

  // Check access to LED/Videowall page after page hydration
  // (needed for TagsSlider to fetch tags).
  if (
    (isLEDMediaPage.value && !hasAccessToLEDPage($currentUser, tagStore.ledTags)) ||
    (isVideowallMediaPage.value && !hasAccessToVideowallPage($currentUser, tagStore.videowallTags))
  ) {
    $utils.mainLoader(false);
    $router.push('/menu');
    return;
  }

  if (opts?.force || opts?.goToFirstPage) {
    mediaListPage.goToPage(0);
  }

  if (opts?.force || opts?.mergeStagedUpdates) {
    // Merge staged updates into the canonical lists before
    // re-initializing.
    mediaListPage.mergeStagedMediaUpdates();
  }

  await mediaListPage.initialize(opts?.force);
  // Hide the main loader after the media list is initialized
  $utils.mainLoader(false);
}

/**
 * Triggers the file uploader dropzone click to open the file picker dialog.
 * Composition API equivalent of `handleFileUploadIconClick` in the legacy media page:
 * @see {@link src/ui/components/pages/create/media/media.js} - `handleFileUploadIconClick`
 */
function uploadButtonClick () {
  fileUploaderStore.setUploaderConfig({
    triggerClickDropzone: true,
    targetComponent: isAlertsPage.value ? 'alertPage' : 'filesPage'
  });
}

onMounted(async () => {
  // Set the page-type context derived from the mediaComponentAs
  // prop. This must happen before mediaListPage is created
  // so that when mediaListPage.initialize(), the shared
  // mediaQueryStore already knows whether this is an
  // LED/Videowall/Alert page.
  mediaQuery.setPageContext(props.mediaComponentAs);

  // Setup media list states before refreshMediaPage()
  // This is to ensure that go to first page and resolving
  // staged media updates to canonical list.
  mediaListPage.goToPage(0);
  mediaListPage.mergeStagedMediaUpdates();

  // Call to get auth user session and fetch necessary data for media page
  proxy.isAuthenticated({
    callback: async () => {
      isPageReady.value = true;
      await refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true });
      await userSchedules.fetchSchedules(isAlertsPage.value);
    }
  });
});

// Reset page context when this component unmounts to prevent it from
// leaking into the next page that mounts and uses the same store.
onUnmounted(() => {
  mediaListPage.cancelFetch();
  mediaListPage.resetScheduledFilter();
  mediaListPage.mergeStagedMediaUpdates();
  isPageReady.value = false;
  mediaQuery.setPageContext();
});
</script>

<template>
  <!-- Important: The 'page' and 'white' CSS classes must be retained as they serve as reference points for calculating the tagSlider height. See updatePageComponentStyle() in utils.js for implementation details. -->
  <div
    id="media-page"
    class="relative flex justify-center h-full w-full px-20 max-lg:px-6 pt-6 pb-14 page white"
  >
    <div
      v-if="pageNotification"
      class="absolute top-0 right-0 z-30 m-4"
    >
      <BaseNotificationAlertsItem
        :notif-id="pageNotification.id"
        :type="pageNotification.type"
        :icon="pageNotification.icon"
        :title="pageNotification.title"
        :message="pageNotification.message"
        :duration="pageNotification.duration"
        :closable="pageNotification.closable"
        @dismiss="pageNotification = null"
      />
    </div>
    <!-- Main Content -->
    <main
      v-if="isPageReady"
      ref="mediaListContainerRef"
      class="media-list-container flex flex-col h-full gap-4 max-w-7xl flex-1 min-w-0"
      :class="{
        'mr-5': showAside
      }"
    >
      <!-- Filters -->
      <div
        class="w-full relative bg-white p-4 pb-0"
        id="media-list-filter-container"
      >
        <!-- Toggle List View mode -->
        <div class="flex flex-row w-full items-start justify-between mb-4">
          <div class="flex items-center gap-4">
            <MediaSearch
              @apply="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
              @clear="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
            />
            <div
              class="items-center gap-4"
              :class="{
                'max-[1599px]:hidden min-[1600px]:flex': showAside,
                'flex': !showAside
              }"
            >
              <MediaFilter
                @apply="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
                @clear="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
              />
              <MediaSort
                @apply="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
                @clear="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
              />
              <MediaCanvasSelector
                :media-component-as="mediaComponentAs"
                :disabled-new-canvas="!showCreateLEDResolution"
                @apply="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
                @led-tag-remove="handleLedTagRemove"
                @new-led-resolution-click="handleNewResolutionClick('led')"
                @new-videowall-resolution-click="handleNewResolutionClick('videowall')"
              />
            </div>
          </div>
          <div class="flex items-center gap-2">
            <FwbButtonGroup>
              <FwbTooltip placement="bottom" theme="light">
                <template #trigger>
                  <FwbButton
                    class="justify-center focus:ring-transparent!"
                    data-component-element-id="media-grid-view-btn"
                    color="light"
                    square
                    size="lg"
                    @click="mediaListPage.toggleListViewMode('grid')"
                  >
                    <BaseIconSvg
                      class="w-4! h-4!"
                      :class="{ 'text-arya-500': listViewMode === 'grid' }"
                      name="flowbite:grid-outline"
                    />
                  </FwbButton>
                </template>
                <template #content>
                  {{ $t('label.grid') }}
                </template>
              </FwbTooltip>
              <FwbTooltip placement="bottom" theme="light">
                <template #trigger>
                  <FwbButton
                    class="justify-center focus:ring-transparent!"
                    data-component-element-id="media-table-view-btn"
                    color="light"
                    square
                    size="lg"
                    @click="mediaListPage.toggleListViewMode('table')"
                  >
                    <BaseIconSvg
                      class="w-4! h-4!"
                      :class="{ 'text-arya-500': listViewMode === 'table' }"
                      name="flowbite:list-outline"
                    />
                  </FwbButton>
                </template>
                <template #content>
                  {{ $t('template.list') }}
                </template>
              </FwbTooltip>
            </FwbButtonGroup>

            <FwbButton
              class="justify-center focus:ring-transparent!"
              data-component-element-id="media-refresh-btn"
              color="light"
              square
              size="lg"
              @click="refreshMediaPage({ force: true })"
            >
              <BaseIconSvg
                class="w-4! h-4!"
                name="flowbite:refresh-outline"
              />
            </FwbButton>

            <FwbButton
              v-if="showCreateProjectBtn"
              class="justify-center focus:ring-transparent! flex-1"
              color="light"
              data-component-element-id="media-create-project-btn"
              @click="handleCreateLEDClick"
            >
              <template #prefix>
                <BaseIconSvg
                  name="flowbite:pen-solid"
                  class="w-4! h-4!"
                />
              </template>
              {{  $t('label.create') }}
            </FwbButton>

            <FwbButton
              class="justify-center border-none focus:ring-transparent! flex-1"
              color="blue"
              data-component-element-id="media-upload-btn"
              @click="uploadButtonClick"
            >
              <template #prefix>
                <BaseIconSvg
                  name="flowbite:cloud-arrow-up-outline"
                  class="w-4! h-4!"
                />
              </template>
              {{  $t('label.upload') }}
            </FwbButton>
          </div>
        </div>
        <div
          class="items-center gap-4 mb-4"
          :class="{
            'max-[1599px]:flex min-[1600px]:hidden': showAside,
            'hidden': !showAside
          }"
        >
          <MediaFilter
            @apply="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
            @clear="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
          />
          <MediaSort
            @apply="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
            @clear="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
          />
          <MediaCanvasSelector
            :media-component-as="mediaComponentAs"
            :disabled-new-canvas="!showCreateLEDResolution"
            @apply="refreshMediaPage({ goToFirstPage: true, mergeStagedUpdates: true })"
            @led-tag-remove="handleLedTagRemove"
            @new-led-resolution-click="handleNewResolutionClick('led')"
            @new-videowall-resolution-click="handleNewResolutionClick('videowall')"
          />
        </div>
        <!-- Important: The 'w-full' and 'left-0' classes must be added in this instance to override old styles. We can have the old styles removed once the old media page is fully migrated. -->
        <TagSlider
          v-if="showTagsSliderFilter"
          ref="tagSliderElRef"
          class="relative w-full left-0"
          :accessorComponentProp="mediaComponentAs"
          @tag-slider-change="handleSlidersEvent"
        />

        <div class="relative w-full">
          <PopIdentifierSlider
            v-if="showPopIdsSliderFilter"
            ref="popIdSliderElRef"
            class="top-0!"
            :accessorComponentProp="mediaComponentAs"
            @popId-slider-change="handleSlidersEvent"
          />
        </div>

        <div
          v-if="isFetching"
          class="absolute inset-0 opacity-50 bg-white z-20"
          data-component-element-id="media-list-filter-loader-overlay"
        />
      </div>

      <!-- Media Grid -->
      <template v-if="isPageHydrated">
        <div
          v-if="listViewMode === 'grid'"
          id="media-list-container"
          class="flex-1 !w-full h-full overflow-auto"
        >
          <MediaGridList
            :show-loader="showListLoader"
            :items="loadedItems"
            :current-audio-playing="mediaPreviewEvents.playAudio.value"
            :orientation="mediaOrientation"
            :has-selection="hasSelection"
            :selected-ids="selectedIds"
            :enable-delete="enableDelete"
            :enable-copy="enableCopy"
            :enable-edit-project="enableEditProject"
            :led-tag="activeLedTag"
            @edit-name="handleMediaRename"
            @card-click="handleCardClick"
            @delete-click="handleDeleteClick"
            @copy-click="handleCopyClick"
            @edit-click="handleEditClick"
            @video-play="mediaPreviewEvents.handleVideoPlayIconClick"
            @image-preview="mediaPreviewEvents.handleImagePreviewIconClick"
            @audio-play="mediaPreviewEvents.handleAudioPlayIconClick"
            @audio-pause="mediaPreviewEvents.handleAudioPauseIconClick"
            @iframe-mouseenter="mediaPreviewEvents.handleRelayHoverToIframe('mouseover', $event)"
            @iframe-mouseleave="mediaPreviewEvents.handleRelayHoverToIframe('mouseout', $event)"
            @drag-tag-to-media="handleDragTagToMedia"
          />
        </div>
        <div
          v-if="listViewMode === 'table'"
          id="media-list-container"
          class="flex-1 !w-full h-full overflow-x-auto overflow-y-hidden"
        >
          <MediaTableList
            :show-loader="showListLoader"
            :items="loadedItems"
            :current-audio-playing="mediaPreviewEvents.playAudio.value"
            :orientation="mediaOrientation"
            :has-selection="hasSelection"
            :selected-ids="selectedIds"
            :enable-delete="enableDelete"
            :enable-copy="enableCopy"
            :enable-edit-project="enableEditProject"
            :led-tag="activeLedTag"
            @edit-name="handleMediaRename"
            @card-click="handleCardClick"
            @delete-click="handleDeleteClick"
            @copy-click="handleCopyClick"
            @edit-click="handleEditClick"
            @video-play="mediaPreviewEvents.handleVideoPlayIconClick"
            @image-preview="mediaPreviewEvents.handleImagePreviewIconClick"
            @audio-play="mediaPreviewEvents.handleAudioPlayIconClick"
            @audio-pause="mediaPreviewEvents.handleAudioPauseIconClick"
            @iframe-mouseenter="mediaPreviewEvents.handleRelayHoverToIframe('mouseover', $event)"
            @iframe-mouseleave="mediaPreviewEvents.handleRelayHoverToIframe('mouseout', $event)"
            @drag-tag-to-media="handleDragTagToMedia"
          />
        </div>

        <MediaListFooter
          v-if="!showListLoader && loadedItems.length > 0"
          :current-page="currentPage"
          :total-pages="totalPages"
          :is-previous-disabled="!hasPreviousPage || isFetching"
          :is-next-disabled="!hasNextPage || isFetching"
          :min-items-count="mediaListPage.paginatorCounter.value?.minItemsCount ?? 0"
          :max-items-count="mediaListPage.paginatorCounter.value?.maxItemsCount ?? 0"
          :total-items-count="mediaListPage.paginatorCounter.value?.totalItemsCount"
          @page-select="goToMediaPage"
        />
      </template>

      <!-- Image Preview -->
      <ImagePreviewDialog
        v-if="mediaPreviewEvents.imagePreview.value.dialog"
        :imagePreview="mediaPreviewEvents.imagePreview.value"
        @close="mediaPreviewEvents.handleImagePreviewIconClick(null)"
      />

      <!-- Play Video Dialog -->
      <VideoPlayerDialog
        v-if="mediaPreviewEvents.playVideo.value.dialog"
        :playVideo="mediaPreviewEvents.playVideo.value"
        @close="mediaPreviewEvents.handleVideoPlayIconClick(null)"
      />

      <!-- Play Audio Dialog -->
      <AudioPlayer
        v-if="mediaPreviewEvents.playAudio.value.audio"
        :playlistProp="[mediaPreviewEvents.playAudio.value.audio]"
        :updateAudioPlayerProp="mediaPreviewEvents.updateAudioPlayer.value"
        :accessorComponentProp="'mediaFilesPage'"
        :elClassProp="{'media-files-card-audio-player': 1}"
        @audio-player-change="mediaPreviewEvents.handleAudioPlayerChange($event)"
        class="whitespace-nowrap overflow-hidden text-ellipsis text-dark-grey text-base leading-5 relative mb-2 mt-0"
      />
    </main>

    <!-- Context Menu: Media Settings Panel -->
    <aside
      v-if="showAside"
      ref="mediaMenuContainerRef"
      class="relative flex-none w-[330px]! h-full overflow-y-auto"
    >
      <template v-if="isLedResolutionVisible || isVideowallResolutionVisible">
        <!-- New LED Form -->
        <LedResolutionMenu
          v-if="isLedResolutionVisible"
          class="mt-0!"
          show-cancel-btn
          @cancel="hideResolutionMenus"
          @led-resolution-menu-change="handleLedResolutionMenuChange"
        />

        <!-- New Videowall Form -->
        <LedResolutionMenu
          v-if="isVideowallResolutionVisible"
          class="mt-0!"
          resolutionType="videowall"
          show-cancel-btn
          @cancel="hideResolutionMenus"
          @led-resolution-menu-change="handleLedResolutionMenuChange"
        />
      </template>
      <template v-else-if="isContextMenuVisible">
        <loader
          v-if="isFetchingDetails"
          :is-requesting="true"
          :text-label="''"
        />
        <MediaMenu
          show-fluid-layout
          :accessorComponentProp="isLEDMediaPage ? 'ledPage' : 'mediaPage'"
          :isMediaAlertTypeProp="isAlertsPage"
          :formTabStyleProp="true"
          :mediaSelectionProp="selectionDetails"
          :userSchedulesProp="userSchedulesList"
          :confirmForceToDeleteMediaProp="confirmForceToDeleteMedia"
          :mediaPropertiesToHideProp="['delete']"
          :hasNoTemplateProp="hideMediaMenuComponentTemplate"
          :tagsSummaryProp="tagsSummary"
          :popIdsSummaryProp="popIdsSummaryByPage"
          @media-menu-change="handleMediaMenuChange"
        />
      </template>
      <template v-else>
        <div class="relative flex flex-col items-end justify-center gap-3 p-4 rounded-md bg-white w-fit">
          <div class="flex flex-col items-start">
            <span class="text-sm text-start font-semibold text-gray-900">{{ $t('media.mediaViewV2ToLegacyTitle') }}</span>
            <span class="text-sm text-start text-gray-500">{{ $t('media.mediaViewSwitcherDescription') }}</span>
          </div>

          <FwbButton
            class="justify-center focus:ring-transparent!"
            color="light"
            data-component-element-id="media-list-legacy-switcher-btn"
            @click="$emit('toggle-to-legacy-media')"
          >
            <div class="flex items-center gap-2">
              <span>{{ $t('buttons.switchBack') }}</span>
              <BaseIconSvg
                class="w-4! h-4!"
                name="flowbite:arrow-right-outline"
              />
            </div>
          </FwbButton>
        </div>
      </template>
    </aside>

    <FileUploaderProgress
      class="absolute bottom-6 left-6 z-[9]"
      :target-components="[TARGET_COMPONENTS.FILES_PAGE, TARGET_COMPONENTS.ALERT_PAGE, TARGET_COMPONENTS.LED_PAGE, TARGET_COMPONENTS.VIDEOWALL_PAGE]"
    />
  </div>
</template>

<style lang="scss" scoped>
@reference '#tailwind.scss';

.media-list-container {
  .media-files-card-audio-player {
    // Hide audio-player
    @apply top-0! left-0! w-0! h-0! opacity-0! absolute!;
  }

  ::-webkit-scrollbar {
    @apply w-5 h-5;
  }

  ::-webkit-scrollbar-track {
    border-top: 1px solid transparent;
    border-left: 1px solid transparent;
    @apply bg-transparent border-transparent border-l-[1px];
  }

  ::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full border-[6px] border-solid bg-clip-content border-transparent;
  }

  ::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }
}
</style>
